import React, { useMemo } from 'react';

import TextInput from 'app/components/CommonStyled/TextInput';
import { CheckboxCheckIcon } from 'app/components/shared/icons';
import { useFormikContext } from 'formik';

import { getRules, isRequired } from '../config/resourceTypeRules';
import { IResourceSubmissionFieldsResponse } from '../hooks/useResourceSubmissionFields';
import {
  ResourceSubmissionFieldCheckbox,
  ResourceSubmissionFieldCheckboxBackground,
  ResourceSubmissionFieldCheckboxContainer,
  ResourceSubmissionFieldCheckboxLabel,
  ResourceSubmissionFieldCheckboxWrapper,
  ResourceSubmissionFieldSubtitle,
  ResourceSubmissionFieldTitle,
} from '../styled';
import {
  ResourceSubmissionPermissionsContainer,
  ResourceSubmissionPermissionsItemContainer,
  ResourceSubmissionPermissionsItemLabel,
  ResourceSubmissionPermissionsRadioCheckbox,
  ResourceSubmissionPermissionsRadioCheckboxContainer,
  ResourceSubmissionPermissionsWrapper,
} from './styled';

const CLIENT_SHARE_STATUS_LABEL_MAP = new Map<string, string>([
  ['granted', 'Yes, we have permission from the client for this work to be referenced externally.'],
  ['denied', 'No, this work cannot be referenced externally.'],
  ['unknown', 'We currently do not have explicit permission, but we will ask the client.'],
]);

const PRIVACY_STATUS_LABEL_MAP = new Map<string, string>([
  ['open', 'This project can be viewed by anyone within the Collective.'],
  ['blinded', 'This case study has been blinded or modified for the Collective.'],
]);

interface IResourceSubmissionPermissionsProps {
  data: IResourceSubmissionFieldsResponse;
  disabled?: boolean;
  bestOfYear?: number;
}

export default function ResourceSubmissionPermissions({
  data,
  disabled = false,
  bestOfYear,
}: IResourceSubmissionPermissionsProps) {
  const formikContext = useFormikContext();

  const currentRules = useMemo(() => {
    return getRules(formikContext.values['resource_type'], Boolean(bestOfYear));
  }, [formikContext.values['resource_type'], bestOfYear]);

  const isInternalShareStatusRequired = useMemo(() => {
    return isRequired(currentRules, 'internal_share_status');
  }, [currentRules]);

  const isExternalClientShareStatusRequired = useMemo(() => {
    return isRequired(currentRules, 'external_client_share_status');
  }, [currentRules]);

  const isPrivacyStatusError = useMemo<boolean>(() => {
    return (
      formikContext.errors['internal_share_status'] &&
      formikContext.touched['internal_share_status']
    );
  }, [
    formikContext.errors['internal_share_status'],
    formikContext.touched['internal_share_status'],
  ]);

  const isClientShareStatusError = useMemo<boolean>(() => {
    return (
      formikContext.errors['external_client_share_status'] &&
      formikContext.touched['external_client_share_status']
    );
  }, [
    formikContext.errors['external_client_share_status'],
    formikContext.touched['external_client_share_status'],
  ]);

  const handlePrivacyStatusClick = (toggle: boolean) => {
    if (disabled) return;

    formikContext.setFieldValue('internal_share_status', toggle ? 'open' : 'blinded');
  };

  const handleClientShareStatusClick = (status: string) => {
    if (disabled) return;

    formikContext.setFieldValue('external_client_share_status', status);
  };

  return (
    <ResourceSubmissionPermissionsWrapper>
      <ResourceSubmissionPermissionsContainer isError={isPrivacyStatusError}>
        <ResourceSubmissionFieldTitle>Can this be shared internally?</ResourceSubmissionFieldTitle>
        <input
          type="hidden"
          name="internal_share_status"
          value={formikContext.values['internal_share_status']}
        />
        <ResourceSubmissionFieldSubtitle isError={isPrivacyStatusError}>
          {isInternalShareStatusRequired
            ? 'Required. This can be edited later.'
            : 'Optional. This can be edited later.'}
        </ResourceSubmissionFieldSubtitle>
        <ResourceSubmissionFieldCheckboxWrapper>
          <ResourceSubmissionFieldCheckboxContainer>
            <ResourceSubmissionFieldCheckbox
              type="checkbox"
              disabled={disabled}
              checked={formikContext.values['internal_share_status'] === 'open'}
              onChange={(e) => handlePrivacyStatusClick(e.target.checked)}
            />
            <ResourceSubmissionFieldCheckboxBackground>
              <CheckboxCheckIcon fill="var(--color-primary)" />
            </ResourceSubmissionFieldCheckboxBackground>
          </ResourceSubmissionFieldCheckboxContainer>
          <ResourceSubmissionFieldCheckboxLabel>
            All case study details are ok to be shared internally
          </ResourceSubmissionFieldCheckboxLabel>
        </ResourceSubmissionFieldCheckboxWrapper>
      </ResourceSubmissionPermissionsContainer>
      <ResourceSubmissionPermissionsContainer isError={isClientShareStatusError}>
        <ResourceSubmissionFieldTitle>
          Can this be shared by kyu Marketing externally?
        </ResourceSubmissionFieldTitle>
        <input
          type="hidden"
          name="external_client_share_status"
          value={formikContext.values['external_client_share_status']}
        />
        <ResourceSubmissionFieldSubtitle isError={isClientShareStatusError}>
          {isExternalClientShareStatusRequired
            ? 'Required. This can be edited later.'
            : 'Optional. This can be edited later.'}
        </ResourceSubmissionFieldSubtitle>
        {data?.external_client_share_status?.map((status) => {
          return CLIENT_SHARE_STATUS_LABEL_MAP.has(status) ? (
            <ResourceSubmissionPermissionsItemContainer
              key={status}
              disabled={disabled}
              onClick={() => handleClientShareStatusClick(status)}
            >
              <ResourceSubmissionPermissionsRadioCheckboxContainer>
                {formikContext.values['external_client_share_status'] === status ? (
                  <ResourceSubmissionPermissionsRadioCheckbox />
                ) : null}
              </ResourceSubmissionPermissionsRadioCheckboxContainer>
              <ResourceSubmissionPermissionsItemLabel>
                {CLIENT_SHARE_STATUS_LABEL_MAP.get(status)}
              </ResourceSubmissionPermissionsItemLabel>
            </ResourceSubmissionPermissionsItemContainer>
          ) : null;
        })}
        <ResourceSubmissionFieldTitle as="label" htmlFor="resource-sharing-notes">
          Sharing notes
        </ResourceSubmissionFieldTitle>
        <TextInput
          {...formikContext}
          {...{
            id: 'resource-sharing-notes',
            readOnly: disabled,
            tabIndex: disabled ? -1 : undefined,
          }}
          name="sharing_notes"
          placeholder="Please elaborate on what can or can't be shared or any other relevant notes"
          textarea
          required={false}
        />
      </ResourceSubmissionPermissionsContainer>
    </ResourceSubmissionPermissionsWrapper>
  );
}
